export const calculateRegression = (data, bandwidth = 0.3) => {
  // Validate input data
  if (!data || !Array.isArray(data)) {
    console.error('Data is undefined or not an array');
    return {
      regressionLine: [],
      senSlope: NaN,
      senIntercept: NaN,
      message: "Invalid input data."
    };
  }

  // Step 1: Sort data by date if available.
  let sortedData = data;
  let useDate = false;
  if (data.length > 0 && data[0].date) {
    sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
    useDate = true;
  }

  // Step 2: Extract valid data points with x-values.
  // When using dates, x is computed as days elapsed since the first date.
  const validData = sortedData
    .map((d, index) => {
      if (d.value !== null && d.value !== undefined && !isNaN(d.value)) {
        let x;
        if (useDate) {
          const baseDate = new Date(sortedData[0].date);
          const currentDate = new Date(d.date);
          x = (currentDate - baseDate) / (1000 * 3600 * 24);
        } else {
          x = index + 1;
        }
        return { x, y: d.value, date: d.date || null };
      }
      return null;
    })
    .filter(d => d !== null);

  const n = validData.length;
  if (n < 2) {
    return {
      regressionLine: [],
      senSlope: NaN,
      senIntercept: NaN,
      message: "Not enough valid data points."
    };
  }

  // Step 3: Compute Sen's Slope.
  const xVals = validData.map(d => d.x);
  const yVals = validData.map(d => d.y);
  let slopes = [];
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      const slope = (yVals[j] - yVals[i]) / (xVals[j] - xVals[i]);
      slopes.push(slope);
    }
  }
  slopes = slopes.filter(s => !isNaN(s));
  if (slopes.length === 0) {
    return {
      regressionLine: [],
      senSlope: NaN,
      senIntercept: NaN,
      message: "Unable to compute Sen's slope."
    };
  }
  slopes.sort((a, b) => a - b);
  const medianSlope = slopes.length % 2 === 0
    ? (slopes[slopes.length / 2 - 1] + slopes[slopes.length / 2]) / 2
    : slopes[Math.floor(slopes.length / 2)];

 const medianSlopePerYear = medianSlope * 12;

  // Compute intercepts for each valid data point and take the median.
  const intercepts = validData.map(d => d.y - medianSlope * d.x);
  intercepts.sort((a, b) => a - b);
  const medianIntercept = intercepts.length % 2 === 0
    ? (intercepts[intercepts.length / 2 - 1] + intercepts[intercepts.length / 2]) / 2
    : intercepts[Math.floor(intercepts.length / 2)];

  // Step 4: Compute LOESS smoothing for each valid data point.
  const loessPoints = validData.map((point) => {
    const x0 = point.x;
    const distances = validData.map(p => Math.abs(p.x - x0));
    const k = Math.max(Math.floor(bandwidth * n), 1);
    const sortedDistances = [...distances].sort((a, b) => a - b);
    const d_max = sortedDistances[Math.min(k, sortedDistances.length - 1)];
    const weights = validData.map((p, j) => {
      const d = distances[j];
      if (d_max > 0 && d < d_max) {
        const w = 1 - Math.pow(d / d_max, 3);
        return Math.pow(w, 3);
      } else if (d_max === 0) {
        return 1;
      } else {
        return 0;
      }
    });
    let sum_w = 0, sum_wx = 0, sum_wy = 0, sum_wxx = 0, sum_wxy = 0;
    validData.forEach((p, j) => {
      const w = weights[j];
      sum_w += w;
      sum_wx += w * p.x;
      sum_wy += w * p.y;
      sum_wxx += w * p.x * p.x;
      sum_wxy += w * p.x * p.y;
    });
    let localSlope = 0, localIntercept = 0;
    if (sum_w !== 0) {
      const denominator = sum_wxx - (sum_wx * sum_wx) / sum_w;
      if (denominator !== 0) {
        localSlope = (sum_wxy - (sum_wx * sum_wy) / sum_w) / denominator;
      }
      localIntercept = (sum_wy - localSlope * sum_wx) / sum_w;
    }
    const y_smoothed = localIntercept + localSlope * x0;
    return {
      x: point.x,
      y: y_smoothed,
      originalY: point.y,
      date: point.date
    };
  });

  return {
    regressionLine: loessPoints,
    senSlope: medianSlopePerYear,
    senIntercept: medianIntercept,
    message: "Computed both Sen's slope and LOESS regression line."
  };
};
